import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button";
import FormInput from "components/atoms/input/FormInput";
import SelectBox from "components/atoms/input/SelectBox";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import SpaceMemberAddModal from "components/page/modal/SpaceMemberAddModal";
import SpaceMemberInviteModal from "components/page/modal/SpaceMemberInviteModal";
import SpaceMemberProfileModal from "components/page/modal/SpaceMemberProfileModal";

import {
    useSpace,
    useSpaceMember,
    useSpaceMemberDelete,
    useSpaceMemberInviteAccept,
    useSpaceMemberInviteReject,
    useSpaceMemberUpdate,
    useSpaceUpdate,
    useUser,
} from "hooks/queries/useUser";

import Constants from "utils/constants";
import { dateKr } from "utils/dateUtil";
import { isActiveSpace, isAdminMember, isPrivateSpace } from "utils/user";

const ContentsWrap = styled.div`
    padding: 40px 0 160px 0;
    width: 100%;
    max-width: 1080px;
`;

const Section = styled.div`
    &:not(:first-of-type) {
        margin-top: 20px;
    }
`;

const SectionTitle = styled(Text).attrs({
    withBarType: true,
    variant: "body-md-500",
    style: { display: "flex", alignItems: "center", gap: "8px" },
})``;

const TitleWithButtonWrap = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const SectionContents = styled.div`
    padding: 20px 10px 0 10px;
`;

const SectionContentsText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})``;

const ProfileCard = styled.div`
    display: flex;
    align-items: center;
    gap: 40px;
    max-width: 480px;
`;

const AvatarContainer = styled.div`
    position: relative;

    width: 72px;
    height: 72px;
`;
const AvatarWrap = styled.div`
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: var(--color-Background);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid var(--color-Line);
`;

const AvatarText = styled(Text)`
    font-size: 2.4rem;
    font-weight: 500;
    text-transform: uppercase;
`;

const AvatarImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const CameraBadge = styled.label`
    position: absolute;
    right: 0px;
    bottom: 0px;

    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--color-White);
    border: 1px solid var(--color-Line);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
`;

const HiddenFile = styled.input`
    display: none;
`;

const HelpText = styled(Text)`
    color: var(--color-Grey1);
    font-size: 1.2rem;
    margin-top: 18px;
`;

const SubCategory = styled.div`
    display: flex;
    align-items: center;
    margin: ${(props) => props.$margin || "0 0 20px 0"};
    min-height: 32px;
    gap: 16px;
`;

const SubTitleWrap = styled.div`
    display: flex;
    width: 120px;
    flex-shrink: 0;
`;

const SubTitleText = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
`;

const Row = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    width: 100%;
    gap: 4px;
`;

const DivideLine = styled.div`
    margin: 40px 0;
    width: 100%;
    height: 1px;
    background-color: var(--color-Line);
`;

const TableWrap = styled.div`
    margin: 8px 0 0 0;
    width: 100%;
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    /* overflow: hidden; */
`;

const Table = styled.table`
    width: 100%;
    padding: 0 0 160px 0;
`;

const Tr = styled.tr`
    border-bottom: solid 1px var(--color-Outline);
    /* overflow: hidden; */

    &:last-child {
        border-bottom: transparent;
    }
`;

const Th = styled.th`
    padding: 12px 16px;
    width: ${(props) => props.width};
    background-color: var(--color-Base1);
    text-align: ${(props) => props.$textAlign || "center"};
    border-right: solid 1px var(--color-Grey3);

    &:last-child {
        border-right: transparent;
    }
`;

const ThFlex = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
`;

const Td = styled.td`
    padding: ${(props) => props.$padding || "12px 16px"};
    background-color: var(--color-White);
    text-align: ${(props) => props.$textAlign || "center"};
    border-right: solid 1px var(--color-Grey3);

    &:last-child {
        border-right: transparent;
    }
`;

const ThText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Black)",
})``;

const TdText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})``;

const TdProfileDiv = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TdProfileImg = styled.img`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 8px;
    background-color: var(--color-Background);
`;

const MemberStatusCard = styled.div`
    padding: 16px 20px;
    border-radius: 8px;
    border: 1px solid var(--color-Outline);
    background-color: var(--color-White);

    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const MemberStatusHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const MemberStatusBar = styled.div`
    flex: 1;
    position: relative;
    height: 10px;
    border-radius: 999px;
    background-color: var(--color-Grey2);
    overflow: hidden;
`;

const MemberStatusSegment = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${(props) => props.start || 0}%;
    width: ${(props) => props.width || 0}%;
    background-color: ${(props) => props.bg || "var(--color-Key)"};
`;

const MemberStatusRight = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1.2rem;
    color: var(--color-SubBlack);
`;

const MemberStatusLegend = styled.div`
    display: flex;
    gap: 8px;
    font-size: 1.2rem;
    color: var(--color-SubBlack);
`;

const MemberStatusLegendItem = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &::before {
        content: "";
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: ${(props) => props.dotColor || "var(--color-Grey2)"};
        flex: 0 0 8px;
    }
`;

const InviteBox = styled.div`
    margin-top: 24px;
    padding: 16px 20px 20px 20px;
    border-radius: 4px;
    background-color: var(--color-Base1);
`;

const InviteTitle = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    color: var(--color-Black);
    margin-bottom: 12px;
`;

const InviteList = styled.ul`
    margin: 0 0 16px 18px;
    padding: 0;
    list-style: disc;
`;

const InviteListItem = styled.li`
    font-size: 1.3rem;
    color: var(--color-SubBlack);
    line-height: 1.6;
`;

const InviteButtonRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const QuestionText = styled.p`
    position: relative;
    top: 5px;
    font-size: 1rem;
    font-weight: 500;
    color: var(--color-White);
`;
const TooltipButton = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 16px;
    height: 16px;
    border-radius: 8px;
    border: transparent;
    background-color: var(--color-Grey2);
`;

function BasicInfoTab(props) {
    const { space_uuid } = useParams();
    const navigate = useNavigate();

    const { setToast } = useToast();
    const { setLoading } = useLoading();

    const userQuery = useUser();
    const user = userQuery.data || {};

    const spaceQuery = useSpace();
    const [currentSpace, setCurrentSpace] = useState({});
    const [draftName, setDraftName] = useState("");
    const spaceUpdateMutation = useSpaceUpdate();

    const spaceMemberQuery = useSpaceMember(space_uuid);
    const [currentSpaceMembers, setCurrentSpaceMembers] = useState([]);
    const spaceMemberUpdateMutation = useSpaceMemberUpdate();
    const spaceMemberDeleteMutation = useSpaceMemberDelete();

    const spaceMemberInviteAcceptMutation = useSpaceMemberInviteAccept();
    const spaceMemberInviteRejectMutation = useSpaceMemberInviteReject();

    const [profileModalMemberId, setProfileModalMemberId] = useState(null);

    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showModifyModal, setShowModifyModal] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const isAdmin = isAdminMember(currentSpace?.my_member_info);

    useEffect(() => {
        userQuery.refetch();
        spaceQuery.refetch();
        spaceMemberQuery.refetch();
    }, [space_uuid]);

    useEffect(() => {
        if (spaceMemberQuery.data) {
            setCurrentSpaceMembers(
                [...spaceMemberQuery.data].sort((a, b) => {
                    // 관리자, 초대일 순으로 정렬
                    const aAdmin = isAdminMember(a);
                    const bAdmin = isAdminMember(b);
                    if (aAdmin !== bAdmin) return aAdmin ? -1 : 1;

                    const aInvited = a.invited_at ? new Date(a.invited_at).getTime() : 0;
                    const bInvited = b.invited_at ? new Date(b.invited_at).getTime() : 0;
                    return bInvited - aInvited;
                }),
            );
        }
    }, [spaceMemberQuery.data]);

    useEffect(() => {
        if (spaceQuery.data) {
            const targetSpace = (spaceQuery.data || []).find((space) => space.uuid === space_uuid) || {};
            setCurrentSpace({ ...targetSpace });
            setDraftName(targetSpace.name || "");
        }
    }, [spaceQuery.data, space_uuid]);

    return (
        <>
            <ContentsWrap>
                <Section>
                    <TitleWithButtonWrap>
                        <SectionTitle>스페이스 정보</SectionTitle>
                    </TitleWithButtonWrap>
                    <SectionContents>
                        <ProfileCard>
                            <AvatarContainer>
                                <AvatarWrap>
                                    {currentSpace?.image ? (
                                        <AvatarImg src={currentSpace?.image} alt="profile" />
                                    ) : (
                                        <AvatarText>{currentSpace.name?.trim()?.[0] || "?"}</AvatarText>
                                    )}
                                </AvatarWrap>
                                {isAdmin && (
                                    <CameraBadge htmlFor="space-profile-image-input" title="이미지 업로드">
                                        <Icon name="camera" size={"14"} />
                                    </CameraBadge>
                                )}

                                <HiddenFile
                                    id="space-profile-image-input"
                                    type="file"
                                    accept={["image/png", "image/jpeg", "image/jpg"].join(",")}
                                    onChange={async (e) => {
                                        if (!e.target.files || e.target.files.length === 0) {
                                            return;
                                        }
                                        if (e.target.files.length > 1) {
                                            Alert("warn", "이미지 업로드", "이미지는 한 개만 업로드해주세요.");
                                            return;
                                        }
                                        if (e.target.files[0].size > 2 * 1024 * 1024) {
                                            Alert("warn", "이미지 업로드", "이미지는 2MB 이하로 업로드해주세요.");
                                            return;
                                        }
                                        if (
                                            e.target.files[0].type !== "image/png" &&
                                            e.target.files[0].type !== "image/jpeg" &&
                                            e.target.files[0].type !== "image/jpg"
                                        ) {
                                            Alert(
                                                "warn",
                                                "이미지 업로드",
                                                "이미지는 JPG 또는 PNG 형식으로 업로드해주세요.",
                                            );
                                            return;
                                        }
                                        const file = e.target.files[0];
                                        if (!file) return;

                                        const formData = new FormData();
                                        formData.append("image", file);
                                        formData.append("file_type", "image");
                                        try {
                                            setLoading(true);
                                            await spaceUpdateMutation.mutateAsync({
                                                space_uuid,
                                                formData,
                                            });
                                            setToast("프로필 이미지가 변경되었습니다.", "info");
                                        } catch (error) {
                                            Alert(
                                                "warn",
                                                "에러",
                                                error.response.data.error || "서버에 오류가 발생하였습니다.",
                                            );
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                />
                            </AvatarContainer>

                            <div style={{ flex: 1 }}>
                                <SubCategory $margin="0 0 8px 0">
                                    <SubTitleWrap>
                                        <SubTitleText>스페이스 이름</SubTitleText>
                                    </SubTitleWrap>
                                    <Row>
                                        {isAdmin ? (
                                            <FormInput
                                                labelDp="none"
                                                height={40}
                                                placeholder="입력해주세요(필수)"
                                                value={draftName}
                                                onChange={(e) => {
                                                    setDraftName(e.target.value);
                                                }}
                                                onBlur={async () => {
                                                    const originalName = currentSpace?.name || "";
                                                    if (draftName === originalName) {
                                                        return;
                                                    }
                                                    const trimmedName = draftName.trim();
                                                    if (trimmedName.length < 1) {
                                                        Alert("warn", "이름", "이름을 입력해주세요.");
                                                        setDraftName(originalName);
                                                        return;
                                                    }
                                                    try {
                                                        setLoading(true);
                                                        await spaceUpdateMutation.mutateAsync({
                                                            space_uuid,
                                                            formData: {
                                                                name: trimmedName,
                                                            },
                                                        });
                                                        setCurrentSpace({
                                                            ...currentSpace,
                                                            name: trimmedName,
                                                        });
                                                        setDraftName(trimmedName);
                                                        setToast("이름이 변경되었습니다.", "info");
                                                    } catch (error) {
                                                        setDraftName(originalName);
                                                        Alert(
                                                            "warn",
                                                            "에러",
                                                            error.response.data.error ||
                                                                "서버에 오류가 발생하였습니다.",
                                                        );
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <SectionContentsText>
                                                {currentSpace.name || "정보 없음"}
                                            </SectionContentsText>
                                        )}
                                    </Row>
                                </SubCategory>

                                <SubCategory $margin="0">
                                    <SubTitleWrap>
                                        <SubTitleText>스페이스 생성일</SubTitleText>
                                    </SubTitleWrap>
                                    <Row>
                                        <SectionContentsText>
                                            {dateKr(currentSpace.created_at) || "정보 없음"}
                                        </SectionContentsText>
                                    </Row>
                                </SubCategory>
                            </div>
                        </ProfileCard>
                        {isAdmin && <HelpText>이미지는 JPG·PNG 형식, 2MB 이하만 가능합니다.</HelpText>}

                        {currentSpace?.my_member_info?.invitation_status ===
                            Constants.SPACE_MEMBER_INVITATION_STATUS_PENDING && (
                            <InviteBox>
                                <InviteTitle>스페이스에 초대 받았습니다.</InviteTitle>
                                <InviteList>
                                    <InviteListItem>
                                        초대를 수락하면 현재 스페이스에서 모든 유료 기능을 사용할 수 있습니다.
                                    </InviteListItem>
                                </InviteList>
                                <InviteButtonRow>
                                    <Button
                                        type="button"
                                        onlyText
                                        width={"84px"}
                                        height={"32px"}
                                        size={"small"}
                                        bgColor={"var(--color-Button4)"}
                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                        fontColor={"var(--color-Black)"}
                                        buttonText={"초대 거절"}
                                        onClick={async () => {
                                            if (!currentSpace?.my_member_info?.id) {
                                                Alert("warn", "에러", "초대 정보를 찾을 수 없습니다.");
                                                return;
                                            }
                                            try {
                                                setLoading(true);
                                                await spaceMemberInviteRejectMutation.mutateAsync({
                                                    space_uuid,
                                                    space_member_id: currentSpace.my_member_info.id,
                                                });
                                                Alert("info", "초대 거절", "초대를 거절했습니다.");
                                                navigate(`/${user.private_space?.uuid}/reference`);
                                            } catch (error) {
                                                Alert(
                                                    "warn",
                                                    "에러",
                                                    error.response?.data?.error || "서버에 오류가 발생하였습니다.",
                                                );
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    />
                                    <Button
                                        type="button"
                                        onlyText
                                        width={"84px"}
                                        height={"32px"}
                                        size={"small"}
                                        bgColor={"var(--color-SubBlack)"}
                                        hoverBgColor={"var(--color-Black)"}
                                        fontColor={"var(--color-White)"}
                                        buttonText={"초대 수락"}
                                        onClick={async () => {
                                            if (!currentSpace?.my_member_info?.id) {
                                                Alert("warn", "에러", "초대 정보를 찾을 수 없습니다.");
                                                return;
                                            }
                                            try {
                                                setLoading(true);
                                                await spaceMemberInviteAcceptMutation.mutateAsync({
                                                    space_uuid,
                                                    space_member_id: currentSpace.my_member_info.id,
                                                });
                                                Alert("info", "초대 수락", "초대를 수락했습니다.");
                                                userQuery.refetch();
                                            } catch (error) {
                                                Alert(
                                                    "warn",
                                                    "에러",
                                                    error.response?.data?.error || "서버에 오류가 발생하였습니다.",
                                                );
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    />
                                </InviteButtonRow>
                            </InviteBox>
                        )}
                    </SectionContents>
                </Section>

                {/* 멤버 현황 */}
                {isAdmin && !isPrivateSpace(currentSpace) && (
                    <>
                        <DivideLine />

                        <Section>
                            <TitleWithButtonWrap>
                                <SectionTitle>멤버 현황</SectionTitle>
                                {isActiveSpace(currentSpace) && (
                                    <Button
                                        type="button"
                                        onlyText
                                        width={"80px"}
                                        height={"24px"}
                                        bgColor={"var(--color-Button1)"}
                                        hoverBgColor={"var(--color-ButtonHover1)"}
                                        fontColor={"var(--color-White)"}
                                        size={"small"}
                                        buttonText={"멤버 수 변경"}
                                        margin={"0 8px 0 0 "}
                                        onClick={() => {
                                            setShowModifyModal(true);
                                        }}
                                    />
                                )}
                            </TitleWithButtonWrap>
                            <SectionContents>
                                {(() => {
                                    const maxMembers = currentSpace.max_member_count;
                                    const adminCount = currentSpaceMembers.filter((u) => isAdminMember(u)).length;
                                    const pendingCount = currentSpaceMembers.filter(
                                        (u) =>
                                            u.is_active &&
                                            u.invitation_status === Constants.SPACE_MEMBER_INVITATION_STATUS_PENDING,
                                    ).length;
                                    const memberCount =
                                        currentSpaceMembers.filter((u) => u.is_active).length -
                                        adminCount -
                                        pendingCount;
                                    const used = adminCount + memberCount + pendingCount;
                                    const availableCount = Math.max(maxMembers - used, 0);

                                    const ratio = (count) => (maxMembers > 0 ? (count / maxMembers) * 100 : 0);

                                    const adminWidth = ratio(adminCount);
                                    const memberWidth = ratio(memberCount);
                                    const pendingWidth = ratio(pendingCount);
                                    const availableWidth = ratio(availableCount);

                                    return (
                                        <MemberStatusCard>
                                            <MemberStatusHeader>
                                                <MemberStatusBar>
                                                    {/* 관리자 */}
                                                    <MemberStatusSegment
                                                        start={0}
                                                        width={adminWidth}
                                                        bg={"var(--color-HighlightPurple)"}
                                                    />

                                                    {/* 멤버 */}
                                                    <MemberStatusSegment
                                                        start={adminWidth}
                                                        width={memberWidth}
                                                        bg={"var(--color-HighlightBlue)"}
                                                    />

                                                    {/* 초대 중 */}
                                                    <MemberStatusSegment
                                                        start={adminWidth + memberWidth}
                                                        width={pendingWidth}
                                                        bg={"var(--color-HighlightYellow)"}
                                                    />

                                                    {/* 초대 가능 */}
                                                    <MemberStatusSegment
                                                        start={adminWidth + memberWidth + pendingWidth}
                                                        width={availableWidth}
                                                        bg={"var(--color-HighlightGreen)"}
                                                    />
                                                </MemberStatusBar>

                                                <MemberStatusRight>
                                                    {used} / {maxMembers}
                                                </MemberStatusRight>
                                            </MemberStatusHeader>

                                            <MemberStatusLegend>
                                                <MemberStatusLegendItem dotColor="var(--color-HighlightPurple)">
                                                    관리자 ({adminCount})
                                                </MemberStatusLegendItem>
                                                <MemberStatusLegendItem dotColor="var(--color-HighlightBlue)">
                                                    멤버 ({memberCount})
                                                </MemberStatusLegendItem>
                                                <MemberStatusLegendItem dotColor="var(--color-HighlightYellow)">
                                                    초대 중 ({pendingCount})
                                                </MemberStatusLegendItem>
                                                <MemberStatusLegendItem dotColor="var(--color-HighlightGreen)">
                                                    초대 가능 ({availableCount})
                                                </MemberStatusLegendItem>
                                            </MemberStatusLegend>
                                        </MemberStatusCard>
                                    );
                                })()}
                            </SectionContents>
                        </Section>
                    </>
                )}

                <DivideLine />
                {/* Email Section */}
                <Section>
                    <TitleWithButtonWrap>
                        <SectionTitle>멤버 목록</SectionTitle>
                        {isAdmin && isActiveSpace(currentSpace) && !isPrivateSpace(currentSpace) && (
                            <Button
                                type="button"
                                onlyText
                                width={"80px"}
                                height={"24px"}
                                bgColor={"var(--color-Button1)"}
                                hoverBgColor={"var(--color-ButtonHover1)"}
                                fontColor={"var(--color-White)"}
                                size={"small"}
                                buttonText={"멤버 초대"}
                                margin={"0 8px 0 0 "}
                                onClick={() => {
                                    setShowInviteModal(true);
                                }}
                            />
                        )}
                    </TitleWithButtonWrap>
                    <SectionContents>
                        <TableWrap>
                            <Table>
                                <tbody>
                                    <Tr>
                                        <Th width="200px" $textAlign="left">
                                            <ThText>프로필</ThText>
                                        </Th>
                                        <Th $textAlign="left">
                                            <ThText>이메일</ThText>
                                        </Th>
                                        <Th width="100px">
                                            <ThFlex>
                                                <ThText>상태</ThText>
                                                <Tooltip message={"관리자, 멤버, 초대 중"}>
                                                    <TooltipButton>
                                                        <QuestionText>?</QuestionText>
                                                    </TooltipButton>
                                                </Tooltip>
                                            </ThFlex>
                                        </Th>
                                        <Th width="100px">
                                            <ThFlex>
                                                <ThText>역할</ThText>
                                                <Tooltip message={"관리자, 에디터, 뷰어"}>
                                                    <TooltipButton>
                                                        <QuestionText>?</QuestionText>
                                                    </TooltipButton>
                                                </Tooltip>
                                            </ThFlex>
                                        </Th>
                                        <Th width="150px">
                                            <ThText>초대일</ThText>
                                        </Th>
                                        <Th width="150px">
                                            <ThText>가입일</ThText>
                                        </Th>
                                        <Th width="80px">
                                            <ThText>관리</ThText>
                                        </Th>
                                    </Tr>
                                    {currentSpaceMembers.map(
                                        (member) =>
                                            member.is_active && (
                                                <Tr key={member.id}>
                                                    <Td>
                                                        <TdProfileDiv>
                                                            {member.invitation_status ===
                                                                Constants.SPACE_MEMBER_INVITATION_STATUS_ACCEPTED && (
                                                                <TdText>
                                                                    {member.image && (
                                                                        <TdProfileImg
                                                                            src={member.image}
                                                                            alt="profile"
                                                                        />
                                                                    )}

                                                                    {member.name}
                                                                </TdText>
                                                            )}
                                                            {user.email === member.email &&
                                                                member.invitation_status ===
                                                                    Constants.SPACE_MEMBER_INVITATION_STATUS_ACCEPTED && (
                                                                    <TdText>
                                                                        <Tooltip message={"프로필 수정"}>
                                                                            <Button
                                                                                onlyIcon
                                                                                bgColor={"transparent"}
                                                                                hoverBgColor={
                                                                                    "var(--color-ButtonHover4)"
                                                                                }
                                                                                onClick={() => {
                                                                                    setShowProfileModal(true);
                                                                                    setProfileModalMemberId(member.id);
                                                                                }}
                                                                            >
                                                                                <Icon
                                                                                    name={"modify"}
                                                                                    size={"12"}
                                                                                    color={"var(--color-Grey1)"}
                                                                                />
                                                                            </Button>
                                                                        </Tooltip>
                                                                    </TdText>
                                                                )}
                                                        </TdProfileDiv>
                                                    </Td>
                                                    <Td $textAlign="left">
                                                        <TdText>{member.email}</TdText>
                                                    </Td>
                                                    <Td>
                                                        <TdText>
                                                            {member.invitation_status ===
                                                            Constants.SPACE_MEMBER_INVITATION_STATUS_PENDING
                                                                ? "초대 중"
                                                                : member.invitation_status ===
                                                                    Constants.SPACE_MEMBER_INVITATION_STATUS_ACCEPTED
                                                                  ? isAdminMember(member)
                                                                      ? "관리자"
                                                                      : "멤버"
                                                                  : "-"}
                                                        </TdText>
                                                    </Td>
                                                    <Td>
                                                        {isAdmin ? (
                                                            isAdminMember(member) ? (
                                                                <TdText>관리자</TdText>
                                                            ) : (
                                                                <SelectBox
                                                                    items={
                                                                        Constants.SPACE_MEMBER_ROLES.filter(
                                                                            (role) =>
                                                                                role.id !==
                                                                                Constants.SPACE_MEMBER_ROLE_ADMIN,
                                                                        ) || []
                                                                    }
                                                                    height={"32px"}
                                                                    margin={"0 8px 0 0"}
                                                                    onChange={async (value) => {
                                                                        try {
                                                                            setLoading(true);
                                                                            await spaceMemberUpdateMutation.mutateAsync(
                                                                                {
                                                                                    space_uuid,
                                                                                    space_member_id: member.id,
                                                                                    formData: {
                                                                                        role: value,
                                                                                    },
                                                                                },
                                                                            );
                                                                            setToast(
                                                                                "멤버 역할이 변경되었습니다.",
                                                                                "info",
                                                                            );
                                                                        } catch (e) {
                                                                            Alert(
                                                                                "warn",
                                                                                "에러",
                                                                                "멤버 역할 변경에 실패했습니다.",
                                                                            );
                                                                        } finally {
                                                                            setLoading(false);
                                                                        }
                                                                    }}
                                                                    width={"100%"}
                                                                    value={member.role}
                                                                />
                                                            )
                                                        ) : (
                                                            <TdText>
                                                                {Constants.SPACE_MEMBER_ROLES.find(
                                                                    (role) => role.id === member.role,
                                                                )?.name || "-"}
                                                            </TdText>
                                                        )}
                                                    </Td>
                                                    <Td>
                                                        <TdText>
                                                            {member?.invited_at ? dateKr(member.invited_at) : "-"}
                                                        </TdText>
                                                    </Td>
                                                    <Td>
                                                        <TdText>
                                                            {member?.started_at ? dateKr(member.started_at) : "-"}
                                                        </TdText>
                                                    </Td>
                                                    <Td>
                                                        {member.is_active && (
                                                            <>
                                                                {isAdmin && !isAdminMember(member) && (
                                                                    <Button
                                                                        onlyText
                                                                        width={"60px"}
                                                                        height={"24px"}
                                                                        bgColor={"var(--color-Button4)"}
                                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                                        fontColor={"var(--color-Black)"}
                                                                        size={"xsmall"}
                                                                        buttonText={"내보내기"}
                                                                        onClick={() => {
                                                                            Confirm(
                                                                                "warn",
                                                                                "알림",
                                                                                `${member.email} 사용자를 제외하시겠습니까?`,
                                                                                "제외",
                                                                                async () => {
                                                                                    try {
                                                                                        setLoading(true);
                                                                                        await spaceMemberDeleteMutation.mutateAsync(
                                                                                            {
                                                                                                space_uuid,
                                                                                                space_member_id:
                                                                                                    member.id,
                                                                                                data: {},
                                                                                            },
                                                                                        );
                                                                                        setToast(
                                                                                            "사용자가 제외되었습니다.",
                                                                                            "info",
                                                                                        );
                                                                                    } catch (error) {
                                                                                        Alert(
                                                                                            "warn",
                                                                                            "에러",
                                                                                            error.response.data.error ||
                                                                                                "서버에 오류가 발생하였습니다.",
                                                                                        );
                                                                                    } finally {
                                                                                        setLoading(false);
                                                                                    }
                                                                                },
                                                                            );
                                                                        }}
                                                                    />
                                                                )}
                                                                {!isAdmin &&
                                                                    user.email === member.email &&
                                                                    member.invitation_status ===
                                                                        Constants.SPACE_MEMBER_INVITATION_STATUS_ACCEPTED && (
                                                                        <Button
                                                                            onlyText
                                                                            width={"60px"}
                                                                            height={"24px"}
                                                                            bgColor={"var(--color-Button4)"}
                                                                            hoverBgColor={"var(--color-ButtonHover4)"}
                                                                            fontColor={"var(--color-Black)"}
                                                                            size={"xsmall"}
                                                                            buttonText={"나가기"}
                                                                            onClick={() => {
                                                                                Confirm(
                                                                                    "warn",
                                                                                    "알림",
                                                                                    "현재 스페이스에서 나가시겠습니까?",
                                                                                    "나가기",
                                                                                    async () => {
                                                                                        try {
                                                                                            setLoading(true);
                                                                                            await spaceMemberDeleteMutation.mutateAsync(
                                                                                                {
                                                                                                    space_uuid,
                                                                                                    space_member_id:
                                                                                                        member.id,
                                                                                                    data: {},
                                                                                                },
                                                                                            );
                                                                                            setToast(
                                                                                                "스페이스에서 탈퇴했습니다.",
                                                                                                "info",
                                                                                            );
                                                                                            navigate("/");
                                                                                        } catch (error) {
                                                                                            Alert(
                                                                                                "warn",
                                                                                                "에러",
                                                                                                error.response.data
                                                                                                    .error ||
                                                                                                    "서버에 오류가 발생하였습니다.",
                                                                                            );
                                                                                        } finally {
                                                                                            setLoading(false);
                                                                                        }
                                                                                    },
                                                                                );
                                                                            }}
                                                                        />
                                                                    )}
                                                            </>
                                                        )}
                                                    </Td>
                                                </Tr>
                                            ),
                                    )}
                                </tbody>
                            </Table>
                        </TableWrap>
                    </SectionContents>
                </Section>
            </ContentsWrap>
            {showInviteModal && <SpaceMemberInviteModal exitModal={(e) => setShowInviteModal(false)} />}
            {showModifyModal && (
                <SpaceMemberAddModal
                    user={user}
                    lastPaymentHistory={props.lastPaymentHistory}
                    exitModal={(e) => setShowModifyModal(false)}
                />
            )}
            {showProfileModal && profileModalMemberId && (
                <SpaceMemberProfileModal
                    exitModal={() => setShowProfileModal(false)}
                    member={currentSpaceMembers.find((member) => member.id === profileModalMemberId)}
                />
            )}
        </>
    );
}

export default BasicInfoTab;
